import { useEffect, useState } from "react";
import { http } from "../api";
import { Search, Users, Activity, X, ShieldCheck, Trash2, Eye, Download, Printer } from "lucide-react";
import { Modal, PageHeader, ImportExportBar, AddButton, FormRow, EmptyState, PersonLink, purgeDraft, showUndoToast } from "../shared";
import PhotoUpload from "./PhotoUpload";
import VoterJourney from "./VoterJourney";

const empty = {
  name: "", gender: "Male", age: "", mobile: "", whatsapp: "", epic: "",
  ward: 7, booth: "42", address: "", profession: "",
  inclination: "Undecided", status: "Incomplete", blood_group: "", language: "auto",
  role: "", is_admin_or_member: false,
  profile_photo: "", cover_photo: "",
  facebook_url: "", instagram_url: "", linkedin_url: "",
  home_location: "", current_location: "",
  household_id: "",
  // Phase 17 – advanced fields
  dob: "",
  income_range: "", house_quality: "", education: "",
  special_skills: [], is_specially_abled: false, disability_type: "",
  is_student: false, student_class: "", special_subjects: [],
  diseases: [], is_patient: false,
  criminal_record: false, criminal_note: "",
  character_note: "", has_character_certificate: false,
  epic_voter_serial: "", voter_roll_page_no: "", voter_roll_page_file_id: "",
};

const INCOME_RANGES = ["Below 1L", "1L-3L", "3L-5L", "5L-10L", "10L+"];
const HOUSE_QUALITIES = ["Kutcha", "Semi-Pucca", "Pucca", "Apartment"];
const EDUCATIONS = ["Illiterate", "Primary", "Secondary", "HS", "Graduate", "PG", "Doctorate"];
const CLASSES = ["Class 1","Class 2","Class 3","Class 4","Class 5","Class 6","Class 7","Class 8","Class 9","Class 10","Class 11","Class 12","College Yr 1","College Yr 2","College Yr 3","College Yr 4","PG Yr 1","PG Yr 2"];

export default function People({ user, pageContext, clearPageContext }) {
  const isAdmin = user && (user.role === "Super Admin" || user.role === "Admin");
  const isSuper = user && user.role === "Super Admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [wardFilter, setWardFilter] = useState("");
  const [boothFilter, setBoothFilter] = useState("");
  const [inclinationFilter, setInclinationFilter] = useState("");
  // Phase 30.7 — recent voter-roll imports filter (hours; null = off)
  const [recentImportHours, setRecentImportHours] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [journeyFor, setJourneyFor] = useState(null);
  const [roles, setRoles] = useState([]);
  const [newRole, setNewRole] = useState("");
  const [households, setHouseholds] = useState([]);
  const [newHhHead, setNewHhHead] = useState("");
  const [selected, setSelected] = useState(new Set()); // contact_ids for bulk actions
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkHh, setBulkHh] = useState("");
  const [bulking, setBulking] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEdit, setBulkEdit] = useState({ inclination: "", booth: "", ward: "", status: "", only_when_blank: false });
  const [rollLightbox, setRollLightbox] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (wardFilter) params.set("ward", wardFilter);
    if (boothFilter) params.set("booth", boothFilter);
    if (inclinationFilter) params.set("inclination", inclinationFilter);
    if (recentImportHours) params.set("voter_roll_recent_hours", String(recentImportHours));
    return http.get(`/contacts?${params.toString()}`).then((r) => setRows(r.data));
  };
  const loadRoles = () => http.get("/roles").then((r) => setRoles(r.data.roles || [])).catch(() => {});
  const loadHouseholds = () => http.get("/households").then((r) => setHouseholds(r.data)).catch(() => {});
  useEffect(() => { load(); }, [q, wardFilter, boothFilter, inclinationFilter, recentImportHours]);
  useEffect(() => { loadRoles(); loadHouseholds(); }, []);

  // Phase 30.9 — auto-apply the recent-imports filter when arriving from an
  // OCR import-success toast so the freshly added voters are one tap away.
  useEffect(() => {
    if (pageContext?.recent_import_hours) {
      setRecentImportHours(Number(pageContext.recent_import_hours));
      if (clearPageContext) clearPageContext();
    }
  }, [pageContext, clearPageContext]);

  const createHousehold = async () => {
    const head = (newHhHead || form.name || "").trim();
    if (!head) return;
    try {
      const r = await http.post("/households", {
        family_head: head,
        address: form.address || "",
        ward: Number(form.ward) || 7,
        booth: form.booth || "42",
      });
      setHouseholds((cur) => [r.data, ...cur]);
      setForm((f) => ({ ...f, household_id: r.data.household_id }));
      setNewHhHead("");
    } catch (e) { alert(e.response?.data?.detail || "Could not create household"); }
  };

  const toggleOne = (cid) => setSelected((cur) => {
    const next = new Set(cur);
    next.has(cid) ? next.delete(cid) : next.add(cid);
    return next;
  });
  const toggleAll = () => setSelected((cur) => cur.size === rows.length ? new Set() : new Set(rows.map((r) => r.contact_id)));
  const clearSel = () => setSelected(new Set());

  const applyBulkLink = async () => {
    if (!bulkHh || selected.size === 0) return;
    setBulking(true);
    try {
      const r = await http.post("/contacts/bulk-link-household", {
        contact_ids: Array.from(selected), household_id: bulkHh,
      });
      setBulkOpen(false); setBulkHh(""); clearSel();
      await load(); await loadHouseholds();
      const auto = r.data.auto_family_head;
      const msg = auto
        ? `Linked ${r.data.modified} people. Auto-suggested ${auto.name} (age ${auto.age}) as family head.`
        : `Linked ${r.data.modified} people to the household.`;
      alert(msg);
    } catch (e) {
      alert(e.response?.data?.detail || "Bulk link failed");
    } finally { setBulking(false); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Move ${selected.size} selected person${selected.size === 1 ? "" : "s"} to the recycle bin?`)) return;
    setBulking(true);
    try {
      const ids = Array.from(selected);
      const r = await http.post("/contacts/bulk-delete", { contact_ids: ids });
      clearSel();
      await load();
      alert(`Moved ${r.data.deleted} of ${r.data.requested} to recycle bin.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Bulk delete failed");
    } finally { setBulking(false); }
  };

  const applyBulkEdit = async () => {
    if (selected.size === 0) return;
    const fields = {};
    if (bulkEdit.inclination) fields.inclination = bulkEdit.inclination;
    if (bulkEdit.booth) fields.booth = bulkEdit.booth;
    if (bulkEdit.ward) fields.ward = parseInt(bulkEdit.ward, 10);
    if (bulkEdit.status) fields.status = bulkEdit.status;
    if (Object.keys(fields).length === 0) { alert("Pick at least one field to update."); return; }
    setBulking(true);
    try {
      const r = await http.post("/contacts/bulk-edit", {
        contact_ids: Array.from(selected),
        fields,
        only_when_blank: !!bulkEdit.only_when_blank,
      });
      setBulkEditOpen(false);
      setBulkEdit({ inclination: "", booth: "", ward: "", status: "", only_when_blank: false });
      clearSel();
      await load();
      alert(`Updated ${r.data.updated} of ${r.data.requested} profile${r.data.requested === 1 ? "" : "s"} on: ${(r.data.fields || []).join(", ")}.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Bulk edit failed");
    } finally { setBulking(false); }
  };

  const addCustomRole = async () => {
    const n = newRole.trim();
    if (!n) return;
    try { const r = await http.post("/roles", { role: n }); setRoles(r.data.roles); setNewRole(""); setForm((f) => ({ ...f, role: n })); } catch { /* noop */ }
  };

  const openNew = () => { setEditing(null); setForm(empty); setSaveError(""); setShow(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r, age: r.age || "" }); setSaveError(""); setShow(true); };

  const openJourney = (c) => setJourneyFor(c);

  const save = async () => {
    setSaving(true); setSaveError("");
    try {
      const payload = { ...form, ward: Number(form.ward) || 7, age: form.age ? Number(form.age) : null };
      if (editing) await http.patch(`/contacts/${editing.contact_id}`, payload);
      else await http.post("/contacts", payload);
      purgeDraft(editing ? `person-${editing.contact_id}` : "person-new");
      setShow(false); load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setSaveError(typeof msg === "string" ? msg : "Save failed. Please check the fields and try again.");
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    try {
      const r = await http.delete(`/contacts/${c.contact_id}`);
      const trashId = r.data?.trash_id;
      load();
      showUndoToast({
        label: `${c.name} moved to recycle bin`,
        hint: trashId ? "Tap Undo to restore, or manage in Recycle bin." : "Ask a Super Admin to restore from Recycle bin.",
        onUndo: trashId ? (async () => {
          try {
            await http.post(`/recycle-bin/${trashId}/restore`);
            load();
          } catch (e) { window.alert(e.response?.data?.detail || "Restore failed"); }
        }) : null,
      });
    } catch (e) {
      window.alert(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <>
      <PageHeader eyebrow="WARD 7 · NABADWIP MUNICIPALITY" title="People" subtitle="Individuals and voter profiles across all booths"
        actions={<><ImportExportBar entity="contacts" onImported={load} /><AddButton onClick={openNew} label="Add person" testid="people-add-button" /></>} />

      <section className="panel directory">
        <div className="directory-toolbar">
          <div className="inner-search">
            <Search size={17} />
            <input data-testid="people-search-input" placeholder="Search by name, mobile or EPIC" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="filters people-filters" data-testid="people-filters">
            <select
              data-testid="people-ward-filter"
              className="chip-select"
              value={wardFilter}
              onChange={(e) => setWardFilter(e.target.value)}
            >
              <option value="">All wards</option>
              {[...new Set(rows.map((r) => r.ward).filter((w) => w != null))].sort((a, b) => a - b).map((w) => (
                <option key={w} value={w}>Ward {w}</option>
              ))}
            </select>
            <select
              data-testid="people-booth-filter"
              className="chip-select"
              value={boothFilter}
              onChange={(e) => setBoothFilter(e.target.value)}
            >
              <option value="">All booths</option>
              {[...new Set(rows.map((r) => r.booth).filter(Boolean))].sort().map((b) => (
                <option key={b} value={b}>Booth {b}</option>
              ))}
            </select>
            <select
              data-testid="people-inclination-filter"
              className="chip-select"
              value={inclinationFilter}
              onChange={(e) => setInclinationFilter(e.target.value)}
            >
              <option value="">All inclinations</option>
              <option value="Supporter">Supporter</option>
              <option value="Neutral">Neutral</option>
              <option value="Opposition">Opposition</option>
              <option value="Undecided">Undecided</option>
            </select>
            <select
              data-testid="people-recent-import-filter"
              className="chip-select"
              value={recentImportHours ?? ""}
              onChange={(e) => setRecentImportHours(e.target.value ? Number(e.target.value) : null)}
              title="Filter to voters imported from voter roll within the selected window"
            >
              <option value="">All time (imports)</option>
              <option value="1">Voter-roll imports · last 1 hour</option>
              <option value="6">Voter-roll imports · last 6 hours</option>
              <option value="24">Voter-roll imports · last 24 hours</option>
              <option value="168">Voter-roll imports · last 7 days</option>
            </select>
            {(wardFilter || boothFilter || inclinationFilter || recentImportHours) && (
              <button
                className="chip-clear"
                data-testid="people-filters-clear"
                onClick={() => { setWardFilter(""); setBoothFilter(""); setInclinationFilter(""); setRecentImportHours(null); }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        {rows.length === 0 ? <EmptyState icon={Users} title="No people yet" hint="Add a person or import a CSV to begin." /> : (
          <>
            {recentImportHours && isSuper && (
              <div className="recent-import-strip" data-testid="recent-imports-strip">
                <div>
                  <b>{rows.length}</b> voter{rows.length === 1 ? "" : "s"} imported in the last{" "}
                  {recentImportHours < 24 ? `${recentImportHours} hour${recentImportHours === 1 ? "" : "s"}` : `${recentImportHours / 24} day${recentImportHours === 24 ? "" : "s"}`}
                  <small>Tune booth, ward or inclination for the whole batch in one shot.</small>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {selected.size > 0 && (
                    <button className="outline" data-testid="recent-imports-clear-sel" onClick={clearSel}>
                      Clear selection
                    </button>
                  )}
                  <button
                    className="primary"
                    data-testid="recent-imports-bulk-edit"
                    onClick={() => {
                      setSelected(new Set(rows.map((r) => r.contact_id)));
                      setBulkEditOpen(true);
                    }}
                  >
                    <Users size={14} /> Select all {rows.length} &amp; bulk edit
                  </button>
                </div>
              </div>
            )}
          <div className="people-table">
            <div className="tr th">
              {isAdmin && (
                <span className="people-check">
                  <input type="checkbox" data-testid="people-select-all"
                    checked={selected.size > 0 && selected.size === rows.length}
                    ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < rows.length; }}
                    onChange={toggleAll} title="Select all" />
                </span>
              )}
              <span>Person</span><span>Ward / booth</span><span>Inclination</span><span>Actions</span>
            </div>
            {rows.map((p, i) => (
              <div className={`tr ${selected.has(p.contact_id) ? "row-selected" : ""}`} data-testid={`person-row-${i}`} key={p.contact_id}>
                {isAdmin && (
                  <span className="people-check">
                    <input type="checkbox" data-testid={`people-check-${i}`}
                      checked={selected.has(p.contact_id)}
                      onChange={() => toggleOne(p.contact_id)} />
                  </span>
                )}
                <span className="person">
                  <span className="avatar person-avatar">{p.name.split(" ").map((x) => x[0]).join("")}</span>
                  <span>
                    <PersonLink contactId={p.contact_id} name={p.name} testid={`person-link-${i}`} className="person-link-name" />
                    <small>{p.gender} · {p.age || "—"} · {p.profession || "Resident"}{p.household_id ? ` · HH-linked` : ""}
                      {p.guardian_name && (<>
                        {" · "}
                        {p.guardian_relation || "Guardian"}:{" "}
                        <PersonLink contactId={p.guardian_contact_id} name={p.guardian_name} testid={`people-guardian-${p.contact_id}`}>{p.guardian_name}</PersonLink>
                      </>)}
                    </small>
                  </span>
                </span>
                <span>Ward {p.ward} · Booth {p.booth}</span>
                <span><i className={`status-dot ${p.inclination === "Supporter" ? "green" : p.inclination === "Neutral" ? "blue" : p.inclination === "Opposition" ? "red" : "amber"}`} />{p.inclination}</span>
                <span className="row-actions">
                  <span className={`pill ${p.status === "Verified" ? "green" : "amber"}`}>{p.status}</span>
                  <button className="link" data-testid={`journey-person-${i}`} onClick={() => openJourney(p)}><Activity size={12} /> Journey</button>
                  <button className="link" data-testid={`edit-person-${i}`} onClick={() => openEdit(p)}>Edit</button>
                  <button className="link danger" data-testid={`delete-person-${i}`} onClick={() => remove(p)}>Del</button>
                </span>
              </div>
            ))}
          </div>
          </>
        )}
      </section>

      {isAdmin && selected.size > 0 && (
        <div className="bulk-bar" data-testid="bulk-action-bar">
          <span><b>{selected.size}</b> selected</span>
          <button className="primary" onClick={() => setBulkOpen(true)} data-testid="bulk-link-hh"><ShieldCheck size={14} /> Link to household</button>
          {isSuper && (
            <button className="outline" data-testid="bulk-edit-button" onClick={() => setBulkEditOpen(true)}>
              <Users size={14} /> Bulk edit
            </button>
          )}
          {isSuper && (
            <button className="danger" data-testid="bulk-delete-button" onClick={bulkDelete} disabled={bulking}>
              <Trash2 size={14} /> Delete {selected.size}
            </button>
          )}
          <button className="outline" onClick={clearSel} data-testid="bulk-clear">Clear</button>
        </div>
      )}

      {bulkEditOpen && (
        <div className="modal-backdrop" data-testid="bulk-edit-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setBulkEditOpen(false); }}>
          <div className="modal" data-testid="bulk-edit-modal">
            <div className="modal-head">
              <h3>Bulk edit {selected.size} person{selected.size === 1 ? "" : "s"}</h3>
              <button className="icon-button" data-testid="bulk-edit-close" onClick={() => setBulkEditOpen(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p className="hint" style={{ marginBottom: 14 }}>Leave a field blank to <b>skip it</b> (won&apos;t touch the current value).</p>
              <FormRow label="Inclination">
                <select data-testid="bulk-edit-inclination" value={bulkEdit.inclination} onChange={(e) => setBulkEdit({ ...bulkEdit, inclination: e.target.value })}>
                  <option value="">— don&apos;t change —</option>
                  <option value="Supporter">Supporter</option>
                  <option value="Neutral">Neutral</option>
                  <option value="Opposition">Opposition</option>
                  <option value="Undecided">Undecided</option>
                </select>
              </FormRow>
              <FormRow label="Booth / Part No">
                <input data-testid="bulk-edit-booth" placeholder="Leave blank to skip" value={bulkEdit.booth} onChange={(e) => setBulkEdit({ ...bulkEdit, booth: e.target.value })} />
              </FormRow>
              <FormRow label="Ward">
                <input type="number" data-testid="bulk-edit-ward" placeholder="Leave blank to skip" value={bulkEdit.ward} onChange={(e) => setBulkEdit({ ...bulkEdit, ward: e.target.value })} />
              </FormRow>
              <FormRow label="Status">
                <select data-testid="bulk-edit-status" value={bulkEdit.status} onChange={(e) => setBulkEdit({ ...bulkEdit, status: e.target.value })}>
                  <option value="">— don&apos;t change —</option>
                  <option value="Verified">Verified</option>
                  <option value="Incomplete">Incomplete</option>
                  <option value="Do-Not-Contact">Do-Not-Contact</option>
                </select>
              </FormRow>
              <FormRow label="Safety">
                <label className="bulk-edit-safety">
                  <input
                    type="checkbox"
                    data-testid="bulk-edit-only-when-blank"
                    checked={bulkEdit.only_when_blank}
                    onChange={(e) => setBulkEdit({ ...bulkEdit, only_when_blank: e.target.checked })}
                  />
                  <span>Only overwrite where the current field is empty</span>
                </label>
              </FormRow>
            </div>
            <div className="modal-foot">
              <button className="outline" onClick={() => setBulkEditOpen(false)}>Cancel</button>
              <button className="primary" data-testid="bulk-edit-apply" onClick={applyBulkEdit} disabled={bulking}>
                {bulking ? "Applying…" : `Apply to ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {show && (
        <Modal
          title={editing ? "Edit person" : "Add person"}
          onClose={() => setShow(false)}
          testid="people-modal"
          autosaveKey={editing ? `person-${editing.contact_id}` : "person-new"}
          getState={() => form}
          applyState={(s) => setForm({ ...empty, ...(s || {}) })}
          isDirty={() => JSON.stringify(form) !== JSON.stringify(editing ? { ...empty, ...editing, age: editing.age || "" } : empty)}>
          <div className="form-grid">
            <FormRow label="Full name"><input data-testid="form-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Mita Saha" /></FormRow>
            <FormRow label="Gender">
              <select data-testid="form-gender-input" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                <option>Male</option><option>Female</option><option>Other</option>
              </select>
            </FormRow>
            <FormRow label={form.dob ? "Age (auto from DOB)" : "Age (use if no DOB)"}><input data-testid="form-age-input" type="number" value={form.age} disabled={!!form.dob} onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder={form.dob ? "" : "e.g. 42"} /></FormRow>
            <FormRow label="Mobile"><input data-testid="form-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></FormRow>
            <FormRow label="EPIC / Voter ID"><input data-testid="form-epic-input" value={form.epic} onChange={(e) => setForm({ ...form, epic: e.target.value })} /></FormRow>
            <FormRow label="Booth / Part No"><input data-testid="form-booth-input" value={form.booth} onChange={(e) => setForm({ ...form, booth: e.target.value })} /></FormRow>
            <FormRow label="EPIC (Voter) Serial"><input data-testid="form-epic-serial-input" value={form.epic_voter_serial || ""} onChange={(e) => setForm({ ...form, epic_voter_serial: e.target.value })} placeholder="Row serial on the voter roll" /></FormRow>
            <FormRow label="Voter Roll Page No."><input data-testid="form-voter-roll-page-input" value={form.voter_roll_page_no || ""} onChange={(e) => setForm({ ...form, voter_roll_page_no: e.target.value })} placeholder="Printed page number" /></FormRow>
            {form.voter_roll_page_file_id && (
              <FormRow label="Source Voter Roll page">
                <div className="voter-roll-actions" data-testid="voter-roll-actions">
                  <button
                    type="button"
                    className="vr-btn"
                    data-testid="vr-view-btn"
                    onClick={() => setRollLightbox({
                      url: `/api/files/${form.voter_roll_page_file_id}`,
                      name: form.name,
                      page_no: form.voter_roll_page_no,
                    })}
                  ><Eye size={13} /> View</button>
                  <a
                    className="vr-btn"
                    data-testid="vr-save-btn"
                    href={`/api/files/${form.voter_roll_page_file_id}?download=1`}
                    download={`voter-roll-${form.name || "page"}.png`}
                  ><Download size={13} /> Save</a>
                  <button
                    type="button"
                    className="vr-btn"
                    data-testid="vr-print-btn"
                    onClick={() => {
                      const w = window.open("", "print");
                      if (!w) return;
                      const src = `/api/files/${form.voter_roll_page_file_id}`;
                      w.document.write(`<html><head><title>Voter roll — ${form.name || ""}</title><style>body{margin:0}img{width:100%;height:auto}</style></head><body onload="window.print();window.close();"><img src="${src}" /></body></html>`);
                      w.document.close();
                    }}
                  ><Printer size={13} /> Print</button>
                </div>
              </FormRow>
            )}
            <FormRow label="Profession"><input data-testid="form-profession-input" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} /></FormRow>
            <FormRow label="Inclination">
              <select data-testid="form-inclination-input" value={form.inclination} onChange={(e) => setForm({ ...form, inclination: e.target.value })}>
                <option>Supporter</option><option>Neutral</option><option>Undecided</option><option>Opposition</option>
                <option>BJP Worker</option><option>TMC Worker</option><option>CPIM Worker</option>
              </select>
            </FormRow>
            <FormRow label="Status">
              <select data-testid="form-status-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option>Verified</option><option>Incomplete</option>
              </select>
            </FormRow>
            <FormRow label="Blood group"><input data-testid="form-blood-input" value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} /></FormRow>
            <FormRow label="Preferred language">
              <select data-testid="form-language-input" value={form.language || "auto"} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                <option value="auto">Auto (from name script)</option>
                <option value="en">English</option>
                <option value="bn">বাংলা</option>
              </select>
            </FormRow>
          </div>
          <FormRow label="Address"><input data-testid="form-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></FormRow>

          <FormRow label="Household">
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <select data-testid="form-household-select" value={form.household_id || ""} onChange={(e) => setForm({ ...form, household_id: e.target.value })} style={{ flex: 1, minWidth: 200 }}>
                <option value="">— None —</option>
                {households.map((h) => (
                  <option key={h.household_id} value={h.household_id}>
                    {h.family_head || "(No head)"} · Booth {h.booth || "?"}{h.address ? ` · ${h.address}` : ""}
                  </option>
                ))}
              </select>
              <input data-testid="form-household-new" placeholder="New household head name" value={newHhHead} onChange={(e) => setNewHhHead(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
              <button type="button" className="outline" data-testid="form-household-create" onClick={createHousehold} disabled={!newHhHead.trim() && !form.name}>+ Create</button>
            </div>
            <small className="hint">Linking a household populates the Voter Journey&apos;s family tree automatically.</small>
          </FormRow>

          {isAdmin && (
            <div style={{ background: "#f7fbf8", border: "1px solid var(--line)", borderRadius: 12, padding: 12, marginTop: 12 }} data-testid="admin-team-panel">
              <p className="eyebrow" style={{ margin: 0 }}><ShieldCheck size={12} style={{ verticalAlign: "middle" }} /> TEAM ROLE (ADMIN ONLY)</p>
              <div className="form-grid" style={{ marginTop: 8 }}>
                <FormRow label="Role in team">
                  <select data-testid="form-role-select" value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                    <option value="">— Not on team —</option>
                    {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </FormRow>
                <FormRow label="Show on Admin & Members tab">
                  <select data-testid="form-is-team-select" value={form.is_admin_or_member ? "yes" : "no"} onChange={(e) => setForm({ ...form, is_admin_or_member: e.target.value === "yes" })}>
                    <option value="no">No</option><option value="yes">Yes</option>
                  </select>
                </FormRow>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
                <input placeholder="Add a new role (e.g. Youth Wing)" value={newRole} onChange={(e) => setNewRole(e.target.value)} data-testid="new-role-input" style={{ flex: 1 }} />
                <button type="button" className="outline" onClick={addCustomRole} data-testid="new-role-add" disabled={!newRole.trim()}>+ Add role</button>
              </div>
            </div>
          )}

          <div style={{ background: "#fbf6ec", border: "1px solid #eddfbe", borderRadius: 12, padding: 12, marginTop: 12 }} data-testid="communicate-profile-panel">
            <p className="eyebrow" style={{ margin: 0 }}>COMMUNICATE PROFILE (OPTIONAL) — powers the Communicate filters</p>
            <div className="form-grid" style={{ marginTop: 8 }}>
              <FormRow label="Date of birth (YYYY-MM-DD)">
                <input data-testid="form-dob-input" type="date" value={form.dob} onChange={(e) => {
                  const dob = e.target.value;
                  let age = form.age;
                  if (dob) {
                    try {
                      const [y, m, d] = dob.split("-").map(Number);
                      const t = new Date();
                      let a = t.getFullYear() - y - ((t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < d)) ? 1 : 0);
                      if (a >= 0 && a < 130) age = a;
                    } catch { /* noop */ }
                  }
                  setForm({ ...form, dob, age });
                }} />
              </FormRow>
              <FormRow label="Income range">
                <select data-testid="form-income-select" value={form.income_range || ""} onChange={(e) => setForm({ ...form, income_range: e.target.value })}>
                  <option value="">—</option>
                  {INCOME_RANGES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </FormRow>
              <FormRow label="House quality">
                <select data-testid="form-house-select" value={form.house_quality || ""} onChange={(e) => setForm({ ...form, house_quality: e.target.value })}>
                  <option value="">—</option>
                  {HOUSE_QUALITIES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </FormRow>
              <FormRow label="Education">
                <select data-testid="form-education-select" value={form.education || ""} onChange={(e) => setForm({ ...form, education: e.target.value })}>
                  <option value="">—</option>
                  {EDUCATIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </FormRow>
              <FormRow label="Special skills (comma-separated)">
                <input data-testid="form-skills-input" value={(form.special_skills || []).join(", ")} onChange={(e) => setForm({ ...form, special_skills: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Tailoring, Driving, Mason" />
              </FormRow>
              <FormRow label="Specially-abled?">
                <select data-testid="form-abled-select" value={form.is_specially_abled ? "yes" : "no"} onChange={(e) => setForm({ ...form, is_specially_abled: e.target.value === "yes" })}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </FormRow>
              {form.is_specially_abled && (
                <FormRow label="Disability type">
                  <input data-testid="form-disability-input" value={form.disability_type || ""} onChange={(e) => setForm({ ...form, disability_type: e.target.value })} placeholder="Visual / Hearing / Locomotor…" />
                </FormRow>
              )}
              <FormRow label="Student?">
                <select data-testid="form-student-select" value={form.is_student ? "yes" : "no"} onChange={(e) => setForm({ ...form, is_student: e.target.value === "yes" })}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </FormRow>
              {form.is_student && (
                <>
                  <FormRow label="Current class">
                    <select data-testid="form-class-select" value={form.student_class || ""} onChange={(e) => setForm({ ...form, student_class: e.target.value })}>
                      <option value="">—</option>
                      {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </FormRow>
                  <FormRow label="Special subjects (comma-separated)">
                    <input data-testid="form-subjects-input" value={(form.special_subjects || []).join(", ")} onChange={(e) => setForm({ ...form, special_subjects: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Physics, Bengali, Math" />
                  </FormRow>
                </>
              )}
              <FormRow label="Patient?">
                <select data-testid="form-patient-select" value={form.is_patient ? "yes" : "no"} onChange={(e) => setForm({ ...form, is_patient: e.target.value === "yes" })}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </FormRow>
              {form.is_patient && (
                <FormRow label="Diseases (comma-separated)">
                  <input data-testid="form-diseases-input" value={(form.diseases || []).join(", ")} onChange={(e) => setForm({ ...form, diseases: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Diabetes, Hypertension" />
                </FormRow>
              )}
              <FormRow label="Criminal record?">
                <select data-testid="form-criminal-select" value={form.criminal_record ? "yes" : "no"} onChange={(e) => setForm({ ...form, criminal_record: e.target.value === "yes" })}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </FormRow>
              {form.criminal_record && (
                <FormRow label="Criminal record note">
                  <input data-testid="form-criminal-note-input" value={form.criminal_note || ""} onChange={(e) => setForm({ ...form, criminal_note: e.target.value })} placeholder="Section, year, status…" />
                </FormRow>
              )}
              <FormRow label="Character certificate?">
                <select data-testid="form-cc-select" value={form.has_character_certificate ? "yes" : "no"} onChange={(e) => setForm({ ...form, has_character_certificate: e.target.value === "yes" })}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </select>
              </FormRow>
              <FormRow label="Character remark">
                <input data-testid="form-character-note-input" value={form.character_note || ""} onChange={(e) => setForm({ ...form, character_note: e.target.value })} placeholder="Free-form remark for character certificate" />
              </FormRow>
            </div>
          </div>

          <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 12, padding: 12, marginTop: 12 }} data-testid="profile-media-panel">
            <p className="eyebrow" style={{ margin: 0 }}>PROFILE MEDIA & SOCIALS</p>
            <div style={{ display: "flex", gap: 20, marginTop: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
              <PhotoUpload category="profile" value={form.profile_photo} onChange={(v) => setForm({ ...form, profile_photo: v })} shape="circle" label="Profile photo" testid="profile-photo" />
              <div style={{ flex: 1, minWidth: 200 }}>
                <PhotoUpload category="cover" value={form.cover_photo} onChange={(v) => setForm({ ...form, cover_photo: v })} shape="banner" label="Cover photo" testid="cover-photo" />
              </div>
            </div>
            <div className="form-grid" style={{ marginTop: 10 }}>
              <FormRow label="WhatsApp number"><input data-testid="form-whatsapp-input" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+91…" /></FormRow>
              <FormRow label="Facebook URL"><input data-testid="form-facebook-input" value={form.facebook_url} onChange={(e) => setForm({ ...form, facebook_url: e.target.value })} placeholder="https://facebook.com/…" /></FormRow>
              <FormRow label="Instagram URL"><input data-testid="form-instagram-input" value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} placeholder="https://instagram.com/…" /></FormRow>
              <FormRow label="LinkedIn URL"><input data-testid="form-linkedin-input" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="https://linkedin.com/in/…" /></FormRow>
              <FormRow label="Home location"><input data-testid="form-home-input" value={form.home_location} onChange={(e) => setForm({ ...form, home_location: e.target.value })} placeholder="Ward 7, Nabadwip" /></FormRow>
              <FormRow label="Current location"><input data-testid="form-current-input" value={form.current_location} onChange={(e) => setForm({ ...form, current_location: e.target.value })} placeholder="Kolkata" /></FormRow>
            </div>
          </div>
          <div className="form-actions">
            {saveError && <p className="hint" data-testid="form-save-error" style={{ color: "#b91c1c", flexBasis: "100%", marginBottom: 6 }}>{saveError}</p>}
            <button className="outline" data-testid="form-cancel-button" onClick={() => { purgeDraft(editing ? `person-${editing.contact_id}` : "person-new"); setShow(false); }}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.name} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </Modal>
      )}

      {bulkOpen && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setBulkOpen(false); }}>
          <div className="form-modal" data-testid="bulk-link-modal" style={{ width: "min(520px, 96vw)" }}>
            <div className="modal-head">
              <h2>Link {selected.size} people to a household</h2>
              <button onClick={() => setBulkOpen(false)}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              <p className="hint">Everyone selected will be moved to the chosen household. Their existing household link (if any) will be replaced.</p>
              <FormRow label="Household">
                <select data-testid="bulk-hh-select" value={bulkHh} onChange={(e) => setBulkHh(e.target.value)}>
                  <option value="">— pick a household —</option>
                  {households.map((h) => (
                    <option key={h.household_id} value={h.household_id}>
                      {h.family_head || "(No head)"} · Booth {h.booth || "?"}{h.address ? ` · ${h.address}` : ""}
                    </option>
                  ))}
                </select>
              </FormRow>
              <div className="form-actions">
                <button className="outline" onClick={() => setBulkOpen(false)}>Cancel</button>
                <button className="primary" data-testid="bulk-apply" disabled={!bulkHh || bulking} onClick={applyBulkLink}>
                  {bulking ? "Linking…" : `Link ${selected.size} people`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {journeyFor && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setJourneyFor(null); }}>
          <VoterJourney contactId={journeyFor.contact_id} user={user} onClose={() => setJourneyFor(null)} />
        </div>
      )}

      {rollLightbox && (
        <div
          className="modal-backdrop vr-lightbox"
          data-testid="vr-lightbox"
          onClick={(e) => { if (e.target === e.currentTarget) setRollLightbox(null); }}
        >
          <div className="vr-lightbox-panel">
            <div className="vr-lightbox-head">
              <b>Voter roll page {rollLightbox.page_no ? `#${rollLightbox.page_no}` : ""} — {rollLightbox.name}</b>
              <button className="icon-button" data-testid="vr-lightbox-close" onClick={() => setRollLightbox(null)}><X size={16} /></button>
            </div>
            <div className="vr-lightbox-body">
              <img src={rollLightbox.url} alt="Voter roll page" data-testid="vr-lightbox-image" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
